import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { GlassCard, Chip, Skeleton, Modal, Table, PageHead } from "@/v2/admin/ui";
import { Q_TYPE_AR } from "@/v2/lib/rbac";
import { ClipboardList, Eye, Sparkles } from "lucide-react";

function AttemptModal({ aid, onClose }) {
  const { data, isLoading } = useQuery({ queryKey: ["adm-attempt", aid], queryFn: async () => (await api.get(`/admin/attempts/${aid}`)).data });
  if (isLoading || !data) return <Modal title="تفاصيل المحاولة" onClose={onClose} wide testId="attempt-modal"><Skeleton className="h-56 w-full" /></Modal>;
  const a = data.attempt;
  return (
    <Modal title="تفاصيل المحاولة" onClose={onClose} wide testId="attempt-modal">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Chip tone="gold">{data.user?.name}</Chip>
        <Chip tone="muted">{data.challenge_title}</Chip>
        <Chip tone={a.passed ? "emerald" : "red"}>{a.score}% — {a.passed ? "ناجح" : "لم يجتز"}</Chip>
        {a.ai_mode && <Chip tone="emerald"><Sparkles size={11} /> {a.ai_mode === "real" ? "تقييم AI حقيقي" : a.ai_mode === "objective" ? "تصحيح موضوعي" : "تقييم احتياطي"}</Chip>}
        <Chip tone="muted">{(a.at || "").slice(0, 16).replace("T", " ")}</Chip>
      </div>
      {(a.clarity || a.understanding || a.application) && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          {[["الوضوح", a.clarity], ["الفهم", a.understanding], ["التطبيق", a.application]].map(([l, v]) => (
            <div key={l} className="v2-inset p-3 text-center"><div className="text-lg font-extrabold v2-gold-text">{v}%</div><div className="text-[10px] text-[#bcc8cf]">{l}</div></div>
          ))}
        </div>
      )}
      <div className="space-y-3">
        {data.answers.map((x) => (
          <div key={x.id} className="v2-inset p-4">
            <div className="flex items-center gap-2 mb-1"><Chip tone="muted">{Q_TYPE_AR[x.question_type] || x.question_type}</Chip></div>
            <div className="text-sm font-semibold mb-2">{x.question_text}</div>
            <div className="text-xs text-[#bcc8cf]">إجابة الموظف: <span className="text-[#f6f8fa]">{Array.isArray(x.value) ? x.value.map((v) => x.options?.[Number(v)] ?? v).join("، ") : (x.options?.[Number(x.value)] ?? String(x.value))}</span></div>
            {x.correct !== null && x.correct !== undefined && (
              <div className="text-xs text-[#bcc8cf] mt-1">الإجابة الصحيحة: <span className="text-[#91cab8]">{Array.isArray(x.correct) ? x.correct.map((v) => x.options?.[Number(v)] ?? v).join("، ") : (x.options?.[Number(x.correct)] ?? String(x.correct))}</span></div>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}

export default function Results() {
  const [chId, setChId] = useState("");
  const [sel, setSel] = useState(null);
  const { data: challenges = [] } = useQuery({ queryKey: ["adm-challenges"], queryFn: async () => (await api.get("/admin/challenges")).data });
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["adm-attempts", chId],
    queryFn: async () => (await api.get(`/admin/attempts?challenge_id=${chId}`)).data,
  });

  return (
    <div className="space-y-5" data-testid="admin-results">
      <PageHead icon={ClipboardList} title="نتائج الاختبارات" sub="جميع المحاولات مع تفاصيل الإجابات وتقييم الذكاء الاصطناعي." />

      <select value={chId} onChange={(e) => setChId(e.target.value)} data-testid="results-ch" className="v2-input !w-auto text-sm font-bold">
        <option value="">كل التحديات</option>
        {challenges.map((c) => <option key={c.id} value={c.id}>{`اليوم ${c.day} — ${c.title}`}</option>)}
      </select>

      {isLoading ? <Skeleton className="h-72 w-full" /> : rows.length === 0 ? (
        <GlassCard className="p-10 text-center text-[#bcc8cf] text-sm">لا توجد محاولات.</GlassCard>
      ) : (
        <Table head={["التاريخ", "الموظف", "التحدي", "الدرجة", "النتيجة", "التقييم", "الزمن (ث)", ""]} min={860} testId="results-table">
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="text-[#bcc8cf] text-xs" dir="ltr">{(r.at || "").slice(0, 16).replace("T", " ")}</td>
              <td className="font-semibold">{r.user_name}</td>
              <td className="max-w-[220px] truncate">{r.challenge_title}</td>
              <td className="font-extrabold v2-gold-text">{r.score}%</td>
              <td><Chip tone={r.passed ? "emerald" : "red"}>{r.passed ? "ناجح" : "لم يجتز"}</Chip></td>
              <td className="text-xs text-[#bcc8cf]">{r.ai_mode === "real" ? "AI حقيقي" : r.ai_mode === "objective" ? "موضوعي" : r.ai_mode || "—"}</td>
              <td className="text-[#bcc8cf]">{r.duration_seconds || 0}</td>
              <td><button onClick={() => setSel(r.id)} data-testid={`result-view-${r.id}`} className="v2-btn-ghost v2-btn-sm"><Eye size={13} /> تفاصيل</button></td>
            </tr>
          ))}
        </Table>
      )}

      {sel && <AttemptModal aid={sel} onClose={() => setSel(null)} />}
    </div>
  );
}
